import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { FaTrash } from "react-icons/fa";
import * as XLSX from "xlsx";
import {
  fetchFertilizerSales,
  fetchInventory,
  uploadInventory,
  updateInventoryItem,
  deleteInventoryItem,
} from "../api/api";

export default function Inventory({ isSidebarOpen }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [salesRows, setSalesRows] = useState([]);
  const [view, setView] = useState("inventory");
  const [editingRows, setEditingRows] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadedRows, setUploadedRows] = useState([]);
  const [hasUploadPreview, setHasUploadPreview] = useState(false);

  const refreshInventory = async () => {
    const res = await fetchInventory();
    if (res?.isSuccess) {
      setRows(res.inventory || []);
    } else {
      setRows([]);
      alert(res?.message || "Failed to fetch inventory");
    }
  };

  const downloadSalesCsv = () => {
    try {
      const cols = salesColumns || [];
      const headers = cols.map((c) => String(c.header || c.field || ""));
      const fields = cols.map((c) => String(c.field || ""));

      const formatTimeAmPm = (time) => {
        const t = String(time ?? "").trim();
        if (!t) return "";
        const m = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (!m) return t;
        let hh = Number(m[1]);
        const mm = m[2];
        const ss = m[3] ?? "00";
        if (!Number.isFinite(hh)) return t;
        const ampm = hh >= 12 ? "PM" : "AM";
        hh = hh % 12;
        if (hh === 0) hh = 12;
        return `${String(hh).padStart(2, "0")}:${mm}:${ss} ${ampm}`;
      };

      const splitCreatedAt = (createdAt) => {
        const raw = String(createdAt ?? "").trim();
        if (!raw) return { date: "", time: "" };

        // Common formats:
        // 2026-05-02T18:35:55.000Z
        // 2026-05-02 18:35:55
        let s = raw.replace("T", " ").replace("Z", " ").trim();
        // Remove milliseconds if present
        s = s.replace(/\.\d+/, "");
        const parts = s.split(/\s+/).filter(Boolean);
        return {
          date: parts[0] || "",
          time: parts[1] || "",
        };
      };

      const escapeCsv = (v) => {
        const s = v == null ? "" : String(v);
        const needsQuotes = /[\n\r,"]/.test(s);
        const escaped = s.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const lines = [];
      lines.push(headers.map(escapeCsv).join(","));

      const rowsToExport = Array.isArray(salesRows) ? salesRows : [];
      for (const r of rowsToExport) {
        const rowVals = fields.map((f) => {
          if (f === "sale_datetime") {
            const dt = splitCreatedAt(r?.created_at);
            return `${dt.date} ${formatTimeAmPm(dt.time)}`.trim();
          }
          if (f === "insertion_date") {
            const dt = splitCreatedAt(r?.created_at);
            return `${dt.date} ${formatTimeAmPm(dt.time)}`.trim();
          }
          if (f === "notes") {
            return r?.rrn ?? "";
          }
          return r?.[f] ?? "";
        });
        lines.push(rowVals.map(escapeCsv).join(","));
      }

      const csv = `${lines.join("\n")}\n`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const fileName = `sales_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}.csv`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Failed to download sales file");
    }
  };

  const normalizeHeader = (h) =>
    String(h || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const headerAliases = {
    sku: ["sku", "skucode", "skuid", "productsku"],
    category: ["category", "cat"],
    product_name: ["productname", "product", "name", "itemname"],
    unit: ["unit", "uom"],
    min_price_pkr: ["minpricepkr", "minprice", "minpkr", "min"],
    max_price_pkr: ["maxpricepkr", "maxprice", "maxpkr", "max"],
    lower_cap_pkr: ["lowercappkr", "lowercap", "lowercapprice", "lower"],
  };

  const pickHeaderIndex = (headersNorm, logicalName) => {
    const candidates = headerAliases[logicalName] || [logicalName];
    for (const c of candidates) {
      const i = headersNorm.indexOf(c);
      if (i >= 0) return i;
    }
    return -1;
  };

  const detectHeaderRowIndex = (rowsToScan, getCellsFn) => {
    const maxScan = Math.min(rowsToScan.length, 20);
    for (let r = 0; r < maxScan; r += 1) {
      const cells = getCellsFn(rowsToScan[r]);
      if (!cells || cells.length < 2) continue;
      const headersRaw = cells
        .map((c) => String(c ?? "").trim())
        .filter((c) => c !== "");
      if (headersRaw.length < 2) continue;
      const headersNorm = headersRaw.map((h) => normalizeHeader(h));
      const hasSku = pickHeaderIndex(headersNorm, "sku") >= 0;
      const hasCategory = pickHeaderIndex(headersNorm, "category") >= 0;
      const hasName = pickHeaderIndex(headersNorm, "product_name") >= 0;
      if (hasSku && (hasCategory || hasName)) return r;
    }
    return 0;
  };

  const parseNumberLoose = (v) => {
    const s = String(v ?? "").trim();
    if (!s) return null;
    const cleaned = s
      .replace(/[,\s]/g, "")
      .replace(/pkr/gi, "")
      .replace(/rs\.?/gi, "")
      .replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const skuSortParts = (sku) => {
    const s = String(sku ?? "").trim();
    const m = s.match(/^([a-zA-Z]+)\s*0*([0-9]+)\s*$/);
    if (!m) return { prefix: s.toUpperCase(), num: Number.MAX_SAFE_INTEGER };
    return { prefix: String(m[1] || "").toUpperCase(), num: Number(m[2]) };
  };

  const sortBySkuSequence = (items) => {
    return (items || [])
      .map((it, idx) => ({ it, idx }))
      .sort((a, b) => {
        const aa = skuSortParts(a.it?.sku);
        const bb = skuSortParts(b.it?.sku);

        if (aa.prefix !== bb.prefix) return aa.prefix.localeCompare(bb.prefix);
        if (aa.num !== bb.num) return aa.num - bb.num;
        return a.idx - b.idx;
      })
      .map((x) => x.it);
  };

  const parseCsvLine = (line, delimiter) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === delimiter && !inQuotes) {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((val) => String(val ?? "").trim());
  };

  const detectCsvDelimiter = (line) => {
    const candidates = [",", ";", "\t"];
    let best = ",";
    let bestCount = 0;
    for (const d of candidates) {
      const count = parseCsvLine(line, d).length;
      if (count > bestCount) {
        bestCount = count;
        best = d;
      }
    }
    return best;
  };

  const parseCsv = (text) => {
    const lines = String(text || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length < 2) return [];

    const delimiter = detectCsvDelimiter(lines[0]);
    const headerLineIndex = detectHeaderRowIndex(lines, (line) =>
      parseCsvLine(String(line || ""), delimiter)
    );
    const headersRaw = parseCsvLine(lines[headerLineIndex], delimiter);
    const headersNorm = headersRaw.map(normalizeHeader);
    const skuHeaderIdx = pickHeaderIndex(headersNorm, "sku");
    if (skuHeaderIdx < 0) {
      alert(
        `SKU column not found. Using first column as SKU. Detected headers: ${headersRaw.join(
          ", "
        )}`
      );
    }

    const idx = (name) => {
      if (name === "sku" && skuHeaderIdx < 0) return 0;
      return pickHeaderIndex(headersNorm, name);
    };

    const get = (arr, name) => {
      const i = idx(name);
      return i >= 0 ? arr[i] : "";
    };

    const data = [];
    for (let i = headerLineIndex + 1; i < lines.length; i += 1) {
      const row = parseCsvLine(lines[i], delimiter);
      const hasAnyValue = row.some((cell) => String(cell ?? "").trim() !== "");
      if (!hasAnyValue) continue;

      const sku = get(row, "sku");
      data.push({
        sku: sku || `ROW_${i + 1}`,
        category: get(row, "category") || null,
        product_name: get(row, "product_name") || null,
        unit: get(row, "unit") || null,
        min_price_pkr: parseNumberLoose(get(row, "min_price_pkr")),
        max_price_pkr: parseNumberLoose(get(row, "max_price_pkr")),
        lower_cap_pkr: parseNumberLoose(get(row, "lower_cap_pkr")),
      });
    }
    return data;
  };

  const parseXlsx = async (file) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array", cellStyles: true });
    const sheetName = wb.SheetNames && wb.SheetNames.length ? wb.SheetNames[0] : null;
    if (!sheetName) return [];

    const ws = wb.Sheets[sheetName];

    // Handle merged cells - fill merged values into all cells
    if (ws["!merges"] && ws["!merges"].length) {
      for (const merge of ws["!merges"]) {
        const startCell = ws[XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c })];
        if (!startCell) continue;
        for (let R = merge.s.r; R <= merge.e.r; R++) {
          for (let C = merge.s.c; C <= merge.e.c; C++) {
            if (R === merge.s.r && C === merge.s.c) continue;
            ws[XLSX.utils.encode_cell({ r: R, c: C })] = { t: startCell.t, v: startCell.v, w: startCell.w };
          }
        }
      }
    }

    // Read cell-by-cell to ensure no rows are skipped
    const ref = ws["!ref"];
    console.log("[XLSX] Sheet !ref:", ref);
    console.log("[XLSX] Merges:", ws["!merges"] ? ws["!merges"].length : 0);
    if (!ref) return [];
    const range = XLSX.utils.decode_range(ref);
    const matrix = [];
    for (let R = range.s.r; R <= range.e.r; R++) {
      const row = [];
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        row.push(cell ? (cell.w || String(cell.v ?? "")) : "");
      }
      matrix.push(row);
    }
    if (matrix.length < 2) return [];

    const headerRowIndex = detectHeaderRowIndex(matrix, (row) => row || []);
    const headersRaw = (matrix[headerRowIndex] || []).map((h) => String(h ?? "").trim());
    const headersNorm = headersRaw.map((h) => normalizeHeader(h));
    const skuHeaderIdx = pickHeaderIndex(headersNorm, "sku");
    if (skuHeaderIdx < 0) {
      alert(
        `SKU column not found. Using first column as SKU. Detected headers: ${headersRaw.join(
          ", "
        )}`
      );
    }

    const idx = (name) => {
      if (name === "sku" && skuHeaderIdx < 0) return 0;
      return pickHeaderIndex(headersNorm, name);
    };
    const get = (arr, name) => {
      const i = idx(name);
      return i >= 0 ? String(arr[i] ?? "").trim() : "";
    };

    console.log("[XLSX] Total rows in matrix:", matrix.length);
    console.log("[XLSX] Header row index:", headerRowIndex);
    console.log("[XLSX] Headers detected:", headersRaw);
    console.log("[XLSX] Headers normalized:", headersNorm);
    console.log("[XLSX] SKU column index:", skuHeaderIdx);
    console.log("[XLSX] First 3 data rows:", matrix.slice(headerRowIndex + 1, headerRowIndex + 4));

    const data = [];
    for (let r = headerRowIndex + 1; r < matrix.length; r += 1) {
      const row = matrix[r] || [];
      // Check if entire row is empty
      const hasAnyValue = row.some((cell) => String(cell ?? "").trim() !== "");
      if (!hasAnyValue) continue;

      const sku = get(row, "sku");
      data.push({
        sku: sku || `ROW_${r + 1}`,
        category: get(row, "category") || null,
        product_name: get(row, "product_name") || null,
        unit: get(row, "unit") || null,
        min_price_pkr: parseNumberLoose(get(row, "min_price_pkr")),
        max_price_pkr: parseNumberLoose(get(row, "max_price_pkr")),
        lower_cap_pkr: parseNumberLoose(get(row, "lower_cap_pkr")),
      });
    }
    return data;
  };

  const onPickFile = async (file) => {
    if (!file) return;
    try {
      const ext = String(file.name || "").toLowerCase();
      const parsed = ext.endsWith(".xlsx") ? await parseXlsx(file) : parseCsv(await file.text());
      if (!parsed.length) {
        alert("No valid rows found (sku is required)");
        return;
      }
      setUploadedRows(sortBySkuSequence(parsed));
      setHasUploadPreview(true);
    } catch (e) {
      console.error(e);
      alert("Failed to read file");
    }
  };

  const onSaveUpload = async () => {
    if (!uploadedRows.length) {
      alert("No uploaded rows to save");
      return;
    }
    try {
      setUploading(true);
      const res = await uploadInventory(uploadedRows, "append");
      if (!res?.isSuccess) {
        alert(res?.message || "Failed to upload inventory");
        return;
      }
      setHasUploadPreview(false);
      setUploadedRows([]);
      await refreshInventory();
      alert("Inventory uploaded");
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to upload inventory");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        await refreshInventory();
      } catch (e) {
        console.error(e);
        alert("Failed to fetch inventory");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const loadSales = async () => {
      try {
        const res = await fetchFertilizerSales();
        if (res?.isSuccess) {
          const list = Array.isArray(res.sales) ? res.sales : [];
          setSalesRows(
            list
              .slice()
              .sort((a, b) => Number(b?.sale_id || 0) - Number(a?.sale_id || 0))
          );
        } else {
          setSalesRows([]);
          alert(res?.message || "Failed to fetch fertilizer sales");
        }
      } catch (e) {
        console.error(e);
        alert("Failed to fetch fertilizer sales");
      }
    };

    if (view === "sales") {
      loadSales();
    }
  }, [view]);

  const inventoryColumns = useMemo(
    () => [
      { field: "sku", header: "SKU", style: { width: "140px" } },
      { field: "category", header: "Category", style: { width: "180px" } },
      { field: "product_name", header: "Product Name", style: { width: "260px" } },
      { field: "unit", header: "Unit", style: { width: "120px" } },
      {
        field: "min_price_pkr",
        header: "Min Price (PKR)",
        style: { width: "160px" },
      },
      { field: "max_price_pkr", header: "Max Price (PKR)", style: { width: "170px" } },
      { field: "lower_cap_pkr", header: "Lower Cap (PKR)", style: { width: "170px" } },
    ],
    []
  );

  const salesColumns = useMemo(
    () => [
      { field: "sku", header: "SKU", style: { width: "140px" } },
      { field: "category", header: "Category", style: { width: "180px" } },
      { field: "product_name", header: "Product Name", style: { width: "260px" } },
      { field: "unit", header: "Unit", style: { width: "120px" } },
      { field: "sale_datetime", header: "Date & Time", style: { width: "220px" } },
      { field: "insertion_date", header: "Insertion Date", style: { width: "220px" } },
      { field: "MID", header: "MID", style: { width: "160px" } },
      { field: "TID", header: "TID", style: { width: "140px" } },
      { field: "rrn", header: "RRN", style: { width: "170px" } },
      { field: "auth_code", header: "Auth Code", style: { width: "160px" } },
      { field: "notes", header: "Notes", style: { width: "170px" } },
      { field: "merchant_name", header: "Merchant Name", style: { width: "200px" } },
      { field: "cnic", header: "CNIC", style: { width: "160px" } },
      { field: "mobile_number", header: "Mobile Number", style: { width: "170px" } },
      { field: "quantity", header: "Quantity", style: { width: "120px" } },
      {
        field: "per_unit_price",
        header: "Per Unit Price",
        style: { width: "160px" },
      },
      { field: "total_price", header: "Total Price", style: { width: "160px" } },
    ],
    []
  );

  const visibleColumns = useMemo(() => {
    if (view === "sales") {
      return salesColumns;
    }
    return inventoryColumns;
  }, [inventoryColumns, salesColumns, view]);

  const baseToggleBtnStyle = {
    border: "1px solid var(--primary)",
    padding: "10px 16px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "110px",
  };

  const activeToggleBtnStyle = {
    ...baseToggleBtnStyle,
    background: "var(--primary)",
    color: "#fff",
  };

  const inactiveToggleBtnStyle = {
    ...baseToggleBtnStyle,
    background: "transparent",
    color: "var(--primary)",
  };

  const onRowEditComplete = async (e) => {
    const { newData } = e;
    try {
      const res = await updateInventoryItem(newData.id, {
        sku: newData.sku,
        category: newData.category,
        product_name: newData.product_name,
        unit: newData.unit,
        min_price_pkr: newData.min_price_pkr,
        max_price_pkr: newData.max_price_pkr,
        lower_cap_pkr: newData.lower_cap_pkr,
      });

      if (!res?.isSuccess) {
        alert(res?.message || "Failed to update inventory");
        return;
      }

      await refreshInventory();
      alert("Inventory updated");
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to update inventory");
    }
  };

  const textEditor = (options) => (
    <InputText
      value={options.value ?? ""}
      onChange={(e) => options.editorCallback(e.target.value)}
      style={{ width: "100%" }}
    />
  );

  const numberEditor = (options) => (
    <InputNumber
      value={options.value == null || options.value === "" ? null : Number(options.value)}
      onValueChange={(e) => options.editorCallback(e.value)}
      minFractionDigits={0}
      maxFractionDigits={2}
      inputStyle={{ width: "100%" }}
    />
  );

  const onDeleteRow = async (row) => {
    if (!row?.id) return;
    const ok = window.confirm(`Delete this item? (ID: ${row.id})`);
    if (!ok) return;

    try {
      const res = await deleteInventoryItem(row.id);
      if (!res?.isSuccess) {
        alert(res?.message || "Failed to delete inventory");
        return;
      }
      await refreshInventory();
      alert("Inventory deleted");
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to delete inventory");
    }
  };

  if (loading) {
    return (
      <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="loader">Loading inventory...</p>
      </div>
    );
  }

  return (
    <div className={`reporting-container ${isSidebarOpen ? "shifted" : ""}`}>
      <section className="reporting-table-section">
        <style>{`
          .inventory-table .p-row-editor-save { margin-right: 10px; }
          .inventory-table .p-row-editor-save,
          .inventory-table .p-row-editor-save .p-button-icon,
          .inventory-table .p-row-editor-save .pi {
            color: #16a34a !important;
          }
          .inventory-table .p-row-editor-cancel,
          .inventory-table .p-row-editor-cancel .p-button-icon,
          .inventory-table .p-row-editor-cancel .pi {
            color: #dc2626 !important;
          }
          .inventory-table .p-row-editor-init,
          .inventory-table .p-row-editor-save,
          .inventory-table .p-row-editor-cancel {
            transform: translateY(4px);
          }
          .inventory-table .inventory-delete-btn { margin-right: 4px; }
          .inventory-table .inventory-action-cell { display: flex; align-items: center; justify-content: center; }
          .inventory-table .inventory-action-col { padding-left: 4px !important; padding-right: 4px !important; }
        `}</style>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginBottom: "14px",
            padding: "14px 18px 0 18px",
          }}
        >
          {view === "inventory" ? (
            <div style={{ display: "flex", gap: "10px", marginRight: "auto" }}>
              <label
                style={{
                  ...inactiveToggleBtnStyle,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Upload CSV/XLSX
                <input
                  type="file"
                  accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: "none" }}
                  onChange={(e) => onPickFile(e.target.files && e.target.files[0])}
                />
              </label>
              <button
                type="button"
                onClick={onSaveUpload}
                disabled={!hasUploadPreview || uploading}
                style={hasUploadPreview ? activeToggleBtnStyle : inactiveToggleBtnStyle}
              >
                {uploading ? "Saving..." : "Save to DB"}
              </button>
              {hasUploadPreview ? (
                <button
                  type="button"
                  onClick={() => {
                    setHasUploadPreview(false);
                    setUploadedRows([]);
                  }}
                  disabled={uploading}
                  style={inactiveToggleBtnStyle}
                >
                  Cancel Preview
                </button>
              ) : null}
            </div>
          ) : view === "sales" ? (
            <div style={{ display: "flex", gap: "10px", marginRight: "auto" }}>
              <button
                type="button"
                onClick={downloadSalesCsv}
                disabled={!salesRows || salesRows.length === 0}
                style={
                  salesRows && salesRows.length
                    ? inactiveToggleBtnStyle
                    : { ...inactiveToggleBtnStyle, opacity: 0.6, cursor: "not-allowed" }
                }
              >
                Download Sales
              </button>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setView("sales")}
            style={view === "sales" ? activeToggleBtnStyle : inactiveToggleBtnStyle}
          >
            Sales
          </button>
          <button
            type="button"
            onClick={() => setView("inventory")}
            style={
              view === "inventory" ? activeToggleBtnStyle : inactiveToggleBtnStyle
            }
          >
            Inventory
          </button>
        </div>
        <DataTable
          key={view + (hasUploadPreview ? "_preview" : "")}
          value={
            view === "sales"
              ? salesRows
              : hasUploadPreview
              ? uploadedRows
              : rows
          }
          dataKey={
            view === "sales" ? "sale_id" : hasUploadPreview ? "sku" : "id"
          }
          className={view === "inventory" ? "dashboard-table inventory-table" : "dashboard-table"}
          responsiveLayout="scroll"
          sortField={view === "inventory" && !hasUploadPreview ? "sku" : undefined}
          sortOrder={view === "inventory" && !hasUploadPreview ? 1 : undefined}
          editMode={view === "inventory" && !hasUploadPreview ? "row" : undefined}
          editingRows={view === "inventory" && !hasUploadPreview ? editingRows : undefined}
          onRowEditChange={
            view === "inventory" && !hasUploadPreview
              ? (e) => setEditingRows(e.data)
              : undefined
          }
          onRowEditComplete={
            view === "inventory" && !hasUploadPreview ? onRowEditComplete : undefined
          }
          paginator
          rows={10}
          paginatorClassName="custom-paginator"
        >
          {visibleColumns.map((col) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              style={col.style}
              body={
                view === "sales" && (col.field === "sale_datetime" || col.field === "insertion_date")
                  ? (rowData) => {
                      const raw = rowData?.created_at;
                      if (!raw) return "";
                      const s = String(raw).trim();
                      const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
                      const timeChunk = s.includes("T") ? s.split("T")[1] : (s.split(" ")[1] || "");
                      const noZ = String(timeChunk || "").replace(/Z$/i, "");
                      const timePart = noZ.split(".")[0];
                      const m = String(timePart || "").match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
                      if (!m) return `${datePart || ""} ${timePart || ""}`.trim();
                      let hh = Number(m[1]);
                      const mm = m[2];
                      const ss = m[3] ?? "00";
                      if (!Number.isFinite(hh)) return `${datePart || ""} ${timePart || ""}`.trim();
                      const ampm = hh >= 12 ? "PM" : "AM";
                      hh = hh % 12;
                      if (hh === 0) hh = 12;
                      return `${datePart || ""} ${String(hh).padStart(2, "0")}:${mm}:${ss} ${ampm}`.trim();
                    }
                  : view === "sales" && col.field === "notes"
                  ? (rowData) => rowData?.rrn ?? ""
                  : undefined
              }
              editor={
                view === "inventory"
                  ? col.field === "id" || col.field === "sku"
                    ? undefined
                    : col.field === "min_price_pkr" ||
                      col.field === "max_price_pkr" ||
                      col.field === "lower_cap_pkr"
                      ? numberEditor
                      : textEditor
                  : undefined
              }
            />
          ))}
          {view === "inventory" && !hasUploadPreview ? (
            <Column
              rowEditor
              headerStyle={{ width: "74px" }}
              bodyClassName="inventory-action-col"
            />
          ) : null}
          {view === "inventory" && !hasUploadPreview ? (
            <Column
              header=""
              headerStyle={{ width: "54px" }}
              bodyStyle={{ textAlign: "center" }}
              bodyClassName="inventory-action-col"
              body={(rowData) => (
                <div className="inventory-action-cell">
                  <button
                    type="button"
                    className="p-link inventory-delete-btn"
                    onClick={() => onDeleteRow(rowData)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: "6px 6px",
                      lineHeight: 1,
                    }}
                    aria-label="Delete"
                    title="Delete"
                  >
                    <FaTrash size={14} color="#dc2626" style={{ display: "block" }} />
                  </button>
                </div>
              )}
            />
          ) : null}
        </DataTable>
      </section>
    </div>
  );
}
